import type { BehaviorSettingsInput, BehaviorsState, CustomBehaviorInput } from '../index.js'
import type { ScorePreview, ScorePreviewRequest } from '../../scoring/index.js'
import { apiDeleteWithResult, apiGet, apiPatch, apiPost, apiPut } from '../../core/index.js'

/**
 * What the behaviors manager needs from an API. The signed-in client and
 * the platform admin reach the same record through different routes and
 * credentials, so the manager is written against this and given whichever
 * applies. Every change answers with the whole state afterwards.
 */
export interface BehaviorsApi {
  /** Distinguishes one client's cached state from another's. */
  queryKey: readonly unknown[]
  load(): Promise<BehaviorsState>
  saveMaster(settings: BehaviorSettingsInput): Promise<BehaviorsState>
  saveFacility(facilityId: string, overrides: BehaviorSettingsInput): Promise<BehaviorsState>
  createCustom(input: CustomBehaviorInput): Promise<BehaviorsState>
  updateCustom(key: string, changes: Omit<CustomBehaviorInput, 'key'>): Promise<BehaviorsState>
  deleteCustom(key: string): Promise<BehaviorsState>
  /** What settings not yet saved would do to a facility's scores; stores nothing. */
  preview(facilityId: string, request: ScorePreviewRequest): Promise<ScorePreview>
}

/** For the signed-in operator or facility administrator: the API scopes everything from their token. */
export const sessionBehaviorsApi: BehaviorsApi = {
  queryKey: ['behaviors', 'session'],
  load: () => apiGet('/behaviors'),
  saveMaster: (settings) => apiPut('/behaviors/master', { settings }),
  saveFacility: (facilityId, overrides) => apiPut(`/behaviors/facilities/${encodeURIComponent(facilityId)}`, { overrides }),
  createCustom: (input) => apiPost('/behaviors/custom', input),
  updateCustom: (key, changes) => apiPatch(`/behaviors/custom/${encodeURIComponent(key)}`, changes),
  deleteCustom: (key) => apiDeleteWithResult(`/behaviors/custom/${encodeURIComponent(key)}`),
  preview: (facilityId, request) => apiPost(`/facilities/${encodeURIComponent(facilityId)}/scores/preview`, request),
}
